import { getUserInfos } from '@/features/user/api'
import { firebaseApp } from '@/firebase.ts'
import type { Permission } from '@/types/permission.types'
import { setTokenProviders } from '@/utils/axios-client.ts'
import { redirect } from '@tanstack/react-router'
import { getAuth, signInWithEmailAndPassword } from 'firebase/auth'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { queryOptions, useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { queryClient } from '@/utils/query-client'
import {
  AuthContext,
  type AuthState,
  type AuthenticatedAuthState
} from '@/contexts/AuthContext.hook.tsx'

export interface AuthenticatedUser {
  email: string
  firstName: string
  lastName: string
  role: string
  permissions: string[]
  language: 'en' | 'fr'
}

const userInfoQueryOptions = (token?: () => Promise<string>) =>
  queryOptions({
    queryKey: ['_global', 'me'],
    queryFn: async (): Promise<AuthenticatedUser> => {
      const data = await getUserInfos(await token!())
      return {
        email: data.email!, // we can assume the user has an email because they just logged in with it
        firstName: data.firstName,
        lastName: data.lastName,
        role: data.role,
        permissions: data.permissions,
        language: data.language
      }
    },
    refetchOnWindowFocus: false // Don't refetch on window focus
  })

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const { i18n } = useTranslation()
  const [token, setToken] = useState<() => Promise<string>>()
  const [isLoading, setIsLoading] = useState(true)
  const [logoutErrorCode, setLogoutErrorCode] = useState<string | undefined>(undefined)

  const userInfoQuery = useQuery({
    ...userInfoQueryOptions(token),
    enabled: !!token
  })

  useEffect(() => {
    i18n.changeLanguage(userInfoQuery.data?.language)
  }, [i18n, userInfoQuery.data])

  const firebaseAuth = useMemo(() => {
    return getAuth(firebaseApp)
  }, [])

  // Handle auth state changes from Firebase or from Firebase's persistence
  useEffect(() => {
    return firebaseAuth.onAuthStateChanged(async userCredential => {
      try {
        if (userCredential && userCredential.email) {
          // User is signed in
          setToken(() => () => userCredential.getIdToken())
        } else {
          // Clear all the cache from the query client.
          queryClient.clear()
          // User is signed out
          setToken(undefined)
          // Clear company token provider
          setTokenProviders({
            getCompanyToken: null
          })
        }
      } finally {
        setIsLoading(false)
      }
    })
  }, [firebaseAuth])

  const login = useCallback(
    async (username: string, password: string) => {
      setLogoutErrorCode(undefined)
      await signInWithEmailAndPassword(firebaseAuth, username, password)
    },
    [firebaseAuth]
  )

  const logout = useCallback(
    async (errorCode: string | undefined) => {
      await firebaseAuth.signOut()
      setLogoutErrorCode(errorCode)
    },
    [firebaseAuth]
  )

  if (isLoading) {
    return
  }

  const buildAuthenticatedState = (
    user: AuthenticatedUser,
    tokenFn: () => Promise<string>
  ): AuthenticatedAuthState => ({
    status: 'AUTHENTICATED',
    user,
    hasPermissions: (permissions: Permission[]) =>
      permissions.every(p => user.permissions.includes(p)),
    rejectIfMissingPermissions: (requiredPermissions: Permission[]) => {
      if (!requiredPermissions.every(p => user.permissions.includes(p))) {
        throw redirect({
          to: '/forbidden',
          search: { redirect: location.href }
        })
      }
    },
    logout,
    getGlobalToken: tokenFn
  })

  const authentication: AuthState =
    token && userInfoQuery.data
      ? buildAuthenticatedState(userInfoQuery.data, token)
      : token
        ? {
            status: 'PENDING',
            loader: async () => {
              try {
                const user = await queryClient.fetchQuery(userInfoQueryOptions(token))
                return buildAuthenticatedState(user, token)
              } catch {
                // If fetching user info fails, sign out and redirect to login
                await logout('UNKNOWN_ERROR')
                throw redirect({ to: '/login' })
              }
            }
          }
        : { status: 'ANONYMOUS', login, errorCode: logoutErrorCode }

  return <AuthContext.Provider value={authentication}>{children}</AuthContext.Provider>
}
